<template>
  <div
    class="card-row"
    :class="{
      'is-locked': locked,
      'is-editing': editMode,
      'is-archived-source': isArchivedSource,
    }"
  >
    <span
      v-if="editMode && !readOnly"
      class="drag-handle"
      title="Drag to reorder"
      aria-hidden="true"
    >
      <svg viewBox="0 0 16 16" width="14" height="14">
        <circle cx="6" cy="3" r="1.2" fill="currentColor"/>
        <circle cx="10" cy="3" r="1.2" fill="currentColor"/>
        <circle cx="6" cy="8" r="1.2" fill="currentColor"/>
        <circle cx="10" cy="8" r="1.2" fill="currentColor"/>
        <circle cx="6" cy="13" r="1.2" fill="currentColor"/>
        <circle cx="10" cy="13" r="1.2" fill="currentColor"/>
      </svg>
    </span>

    <span class="type-pill" :class="`type-${item.item_type}`">
      {{ typeLabel }}
    </span>

    <span
      v-if="secondaryPill"
      class="input-style-pill mono"
      :title="secondaryPillTitle"
    >{{ secondaryPill }}</span>

    <span v-if="cardCode" class="card-code mono">{{ cardCode }}</span>

    <span class="card-title" :class="{ 'card-title-empty': !preview }">
      <template v-if="preview">{{ preview }}</template>
      <template v-else>(empty)</template>
    </span>

    <span
      v-if="isArchivedSource"
      class="archived-badge"
      title="The referenced source row has been archived"
    >Archived source</span>

    <button
      v-if="editMode && !readOnly"
      type="button"
      class="row-delete"
      title="Remove this item from the questionnaire"
      aria-label="Remove from questionnaire"
      @click.stop="$emit('delete', item)"
    >
      <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
        <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
      </svg>
    </button>
  </div>
</template>

<script>
// QuestionnaireCardRow
//
// Renders one item from the seq_questionnaire_items junction. The `item`
// prop carries the junction row plus the inlined source row (under
// `item.item`) shaped by `item.item_type` ('question' | 'content_card'
// | 'flow_card').
//
// Step 2 (read path): no edit / delete / drag handlers wired. The
// drag handle is hidden when readOnly is true (the section list passes
// readOnly=true during the junction Step 2 cutover; reordering and
// row-level actions come in Step 3).
//
// When the inlined source row's `status` is 'archived', the row gets a
// greyed-out treatment plus an "Archived source" badge. ON DELETE
// RESTRICT on the junction's source FKs guarantees the source actually
// exists; archive is a soft signal that the bank wants this retired.

import { pickItemPreview } from './_data/cards.js';

const FLOW_TYPE_LABELS = {
  welcome: 'Welcome',
  consent_chooser: 'Consent chooser',
  consent_adult: 'Consent (adult)',
  consent_child_parental: 'Consent (parental)',
  consent_child_teacher: 'Consent (teacher)',
  email_capture: 'Email capture',
  email_results: 'Email results',
  email_code: 'Email code',
  thanks: 'Thanks',
};

export default {
  name: 'QuestionnaireCardRow',
  props: {
    item: { type: Object, required: true },
    locked: { type: Boolean, default: false },
    editMode: { type: Boolean, default: false },
    // Junction Step 2: items are read-only. Hides the drag handle and
    // suppresses any per-row mutation affordances. Step 3 flips this
    // to false.
    readOnly: { type: Boolean, default: true },
  },
  emits: ['edit', 'delete'],
  computed: {
    preview() {
      return pickItemPreview(this.item);
    },
    isArchivedSource() {
      return this.item && this.item.item && this.item.item.status === 'archived';
    },
    // Primary pill — broad category. Coloured per item_type in the styles.
    typeLabel() {
      const t = this.item && this.item.item_type;
      if (t === 'question') return 'Question';
      if (t === 'content_card') return 'Content';
      if (t === 'flow_card') return 'Flow';
      return t || '';
    },
    // Secondary mono pill — the precise type within the broad category.
    // For questions: numeric_stepper / single_choice / matrix.
    // For content cards: factoid / recipe / tip / section_complete.
    // For flow cards: a human label for the flow_type slug.
    secondaryPill() {
      const it = this.item;
      if (!it || !it.item) return null;
      if (it.item_type === 'question') return it.item.question_type || null;
      if (it.item_type === 'content_card') return it.item.type || null;
      if (it.item_type === 'flow_card') {
        const k = it.item.flow_type;
        return FLOW_TYPE_LABELS[k] || k || null;
      }
      return null;
    },
    secondaryPillTitle() {
      if (!this.secondaryPill) return '';
      const it = this.item;
      if (it.item_type === 'question') return `Question type: ${this.secondaryPill}`;
      if (it.item_type === 'content_card') return `Card type: ${this.secondaryPill}`;
      return `Flow: ${this.secondaryPill}`;
    },
    // Questions carry an authored code; content cards and flow cards
    // do too but the spec leans on code as a question-specific concept
    // visually. Show whatever code is present on the inlined source.
    cardCode() {
      const inner = this.item && this.item.item;
      return inner && inner.code ? inner.code : '';
    },
  },
};
</script>

<style scoped>
.card-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: var(--color-bg-card);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.card-row:hover:not(.is-locked) {
  border-color: var(--color-border-strong);
  background: var(--color-bg-soft);
}

.card-row.is-editing {
  box-shadow: inset 2px 0 0 var(--color-accent);
}

/* Archived-source treatment: dim the whole row and give it a soft warn
   tint on the border so it's identifiable without being a hard error
   state. The "Archived source" badge below carries the explicit signal. */
.card-row.is-archived-source {
  opacity: 0.65;
  background: var(--color-bg-soft);
}

.drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary);
  cursor: grab;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
}

.drag-handle:active {
  cursor: grabbing;
}

.drag-handle:hover {
  color: var(--color-text-secondary);
}

.type-pill {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 2px 7px;
  border-radius: 4px;
  white-space: nowrap;
}

.type-pill.type-question {
  background: var(--color-accent);
  color: white;
}

.type-pill.type-content_card {
  background: var(--color-warn-soft, rgba(232, 163, 61, 0.18));
  color: var(--color-warn-dark, #6B4A0F);
}

.type-pill.type-flow_card {
  background: rgba(46, 125, 50, 0.12);
  color: #2E7D32;
}

.input-style-pill {
  flex-shrink: 0;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
  background: var(--color-bg-soft);
  color: var(--color-text-secondary);
  border: 0.5px solid var(--color-border);
  white-space: nowrap;
  letter-spacing: 0;
}

.card-code {
  flex-shrink: 0;
  font-size: 12px;
  color: var(--color-text-secondary);
  font-weight: 500;
  min-width: 60px;
}

.mono {
  font-family: var(--font-mono);
}

.card-title {
  flex: 1;
  min-width: 0;
  font-family: inherit;
  font-size: 13px;
  color: var(--color-text-primary);
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-title-empty {
  color: var(--color-text-tertiary);
  font-style: italic;
}

.archived-badge {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 4px;
  background: var(--color-warn-soft, rgba(232, 163, 61, 0.18));
  color: var(--color-warn-dark, #6B4A0F);
  white-space: nowrap;
}

.row-delete {
  flex-shrink: 0;
  background: none;
  border: 0.5px solid transparent;
  border-radius: 4px;
  padding: 4px;
  color: var(--color-text-tertiary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
}

.row-delete:hover {
  color: var(--color-bad, #C8392E);
  background: var(--color-bad-soft, rgba(200, 57, 46, 0.10));
  border-color: var(--color-bad-soft, rgba(200, 57, 46, 0.10));
}

.is-locked {
  opacity: 0.85;
}
</style>
